/**
 * useTopupPacks — React Query hook for fetching the top-up pack list.
 *
 * Implements US-012 / T3: fetches GET /system-config/topup-packs via the billing
 * service and exposes the pack list with loading/error/refetch state.
 *
 * Modelled directly on useParentDashboard (frontend/src/hooks/useParentDashboard.ts).
 *
 * Query key: ['billing', 'topup-packs']
 * staleTime: 5 minutes (300 000 ms) — matches the backend 5-min config cache TTL;
 *   packs change rarely (config admin op, not user action) so 5-min stale is safe.
 *
 * Error handling: isError is exposed; the component (T4) renders ErrorState with
 * onRetry=refetch. Errors are NEVER swallowed here (KB TO-LL-036).
 *
 * CCM-03: this hook is imported only by PlanAndCoinsTab, which mounts only under
 *   ParentGuard — students never trigger this fetch.
 * CCM-04: packs come from the API response; no hardcoded pack data here.
 */

import { useQuery } from '@tanstack/react-query';
import { getTopupPacks } from '@/services/billing';
import type { TopupPack } from '@/types/billing';

/**
 * Return type for the useTopupPacks hook.
 */
export interface UseTopupPacksReturn {
  /** The config-driven list of purchasable top-up packs (empty array during loading) */
  packs: TopupPack[];
  /** True while the initial fetch is in-flight */
  isLoading: boolean;
  /** True when the fetch failed (component should render ErrorState with onRetry=refetch) */
  isError: boolean;
  /** Error object — undefined when no error */
  error: unknown;
  /** Manually trigger a fresh fetch (used by ErrorState retry button) */
  refetch: () => void;
}

/**
 * Hook for fetching the top-up pack list from GET /system-config/topup-packs.
 *
 * Implements US-012 / T3 / AC-5: packs are config-driven from the server response;
 * changing the DynamoDB topup_packs config updates what renders without a code deploy.
 *
 * staleTime 5min aligns with SystemConfigService's 5-min per-key cache so browser
 * and Lambda both cache at the same granularity.
 */
export function useTopupPacks(): UseTopupPacksReturn {
  const { data, isLoading, isError, error, refetch } = useQuery<TopupPack[], unknown>({
    queryKey: ['billing', 'topup-packs'],
    queryFn: () => getTopupPacks(),
    staleTime: 5 * 60 * 1000, // 5 minutes — matches backend SystemConfigService cache TTL
  });

  return {
    packs: data ?? [],
    isLoading,
    isError,
    error,
    refetch: () => {
      refetch();
    },
  };
}
